import { ActionIcon, Badge, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconLink, IconPencil, IconTrash } from '@tabler/icons-react';

import { providerLabel, type ProviderRate } from '@/types/provider-cost';
import { formatRate } from './format';

export interface ProviderRatesTableProps {
  rates: ProviderRate[];
  onEdit: (rate: ProviderRate) => void;
  onDelete: (rate: ProviderRate) => void;
  /** Show the agreement a derived rate belongs to. */
  onOpenAgreement: (agreementId: string) => void;
}

function sortKey(rate: ProviderRate): string {
  return [rate.provider, rate.service, rate.unitKey, rate.effectiveFrom].join('\u0000');
}

/**
 * The rate card, one row per rate, ordered by key then start date so a key's
 * history reads top to bottom. Rates from an agreement are changed through it.
 */
export function ProviderRatesTable({
  rates,
  onEdit,
  onDelete,
  onOpenAgreement,
}: Readonly<ProviderRatesTableProps>) {
  if (rates.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="lg">
        No rates. Usage from providers without a rate is shown as unrated in the cost figures.
      </Text>
    );
  }

  const sorted = [...rates].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  return (
    <Table highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Provider</Table.Th>
          <Table.Th>Service</Table.Th>
          <Table.Th>Unit</Table.Th>
          <Table.Th ta="right">Cost per unit</Table.Th>
          <Table.Th>From</Table.Th>
          <Table.Th>To</Table.Th>
          <Table.Th>Source</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sorted.map((rate) => {
          const fromAgreement = rate.providerAgreementId !== null;
          return (
            <Table.Tr key={rate.id} data-testid={`rate-row-${rate.id}`}>
              <Table.Td>{providerLabel(rate.provider)}</Table.Td>
              <Table.Td>
                <Text size="sm" ff="monospace">
                  {rate.service}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ff="monospace">
                  {rate.unitKey}
                </Text>
              </Table.Td>
              <Table.Td ta="right">{formatRate(rate.costPerUnit, rate.currency)}</Table.Td>
              <Table.Td>{rate.effectiveFrom}</Table.Td>
              <Table.Td>
                {rate.effectiveTo ?? (
                  <Text size="sm" c="dimmed">
                    open
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={fromAgreement ? 'blue' : 'gray'}>
                  {fromAgreement ? 'Agreement' : 'Entered'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap" justify="flex-end">
                  {fromAgreement ? (
                    <Tooltip label="Set by an agreement — change it there">
                      <ActionIcon
                        variant="subtle"
                        aria-label="Show its agreement"
                        onClick={() => onOpenAgreement(rate.providerAgreementId as string)}
                      >
                        <IconLink size={16} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip label="Correct">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Correct ${rate.service}/${rate.unitKey}`}
                          onClick={() => onEdit(rate)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Delete ${rate.service}/${rate.unitKey}`}
                          onClick={() => onDelete(rate)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
